<template>
  <div>
    <StatusBtns
      :on-change="statusHandler"
      :back-btn-label="backBtnLabel"
      :next-btn-label="nextBtnLabel"
      :back-forward="backForwardHandler"
      :disabled="selectedItems.length < 1"
    />

    <div class="mt-3" />
    <Datepicker
      v-if="selectedDate"
      :available-dates="store.date_picker_dates"
      :selected="selectedDate"
      @change="dateHandler"
    />
    <div class="card ma-5">
      <div class="text-center" v-if="isDisplayRequestLimit">{{ requestLimitDate }}まで回答可能です</div>
    </div>

    <div class="d-flex justify-end mr-3">
      <BackForwardBtn
        v-if="mobile"
        :back-btn-label="backBtnLabel"
        :next-btn-label="nextBtnLabel"
        :on-click="backForwardHandler"
        :disabled="selectedItems.length < 1"
      />
    </div>

    <div class="d-flex justify-end mr-3">
      <GroupByBtns
        v-if="enableOrderBy"
        :on-change="groupByHandler"
      />
    </div>

    <div class="ma-3" style="overflow-x: auto">
      <v-data-table
        v-if="status === 'confirmed' && groupBy === 'order_item'"
        :headers="headers"
        :items="groupedItems"
        :mobile-breakpoint="0"
        :items-per-page="50"
        :footer-props="{
          'items-per-page-options': [10,50,100,-1],
          'items-per-page-all-text': $t('all'),
          'items-per-page-text': $t('items_per_page')
        }"
      >
        <template v-slot:[`item.note`]="{ item }">
          <span class="text-caption">{{ item.note }}</span>
        </template>
      </v-data-table>

      <Table v-else :headers="headers" :items="items" :on-checked="onChecked"
      :on-click="clickHandler" :show-check-box="isShowCheckBox" :hiddenLabel="true"
      :isGroupByShop="isGroupByShop" disableSort groupBy="group_by_id"/>
    </div>
  </div>
</template>

<script>
import StatusBtns from "@/components/farm/orderItems/StatusBtns.vue";
import GroupByBtns from "@/components/farm/orderItems/GroupByBtns.vue";
import Datepicker from "@/components/shared/Datepicker.vue";
import Table from "@/components/shared/Table.vue";
import BackForwardBtn from "@/components/farm/orderItems/BackForwardBtn.vue";
import statusBtnLabel from "@/helpers/statusBtnLabel";
import { formatDate } from "@/plugins/i18n";

export function toDateKey(date) {
  return formatDate(date, "yyyy-mm-dd");
}

export default {
  components: { StatusBtns, GroupByBtns, Datepicker, Table, BackForwardBtn },
  data() {
    return {
      mobile: this.$vuetify.breakpoint.xs,
      status: "unconfirmed",
      groupBy: "order_item",
      headers: [
        { text: this.$t("id"), value: "id", width: "60px" },
        { text: this.$t("min_count"), value: "min_count", width: "60px" },
        { text: this.$t("set_count"), value: "set_count", width: "60px" },
        { text: this.$t("unit_price"), value: "unit_price", width: "40px" },
        { text: this.$t("shop_name"), value: "shop_name", width: "80px" }
      ],
      items: [],
      selectedDate: null,
      selectedItems: [],
      nextBtnLabel: "",
      backBtnLabel: "",
      isShowCheckBox: true,
      currentDate: ""
    };
  },
  computed: {
    store() {
      return this.$store.getters["orderItems/getState"];
    },
    startDate() {
      return this.store.start_date
        ? this.$date.MMDD(this.store.start_date)
        : "-";
    },
    endDate() {
      return this.store.end_date ? this.$date.MMDD(this.store.end_date) : "-";
    },
    closestNextWorkingDate() {
      return this.store.closest_next_working_date;
    },
    isDisplayRequestLimit() {
      return this.status === "unconfirmed" && this.selectedDate;
    },
    requestLimitDate() {
      if (!this.selectedDate) {
        return false;
      }
      const selectedDate = new Date(this.selectedDate);
      const requestLimitDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate() - 1);
      return formatDate(requestLimitDate, "yyyy年mm月dd日 16:00");
    },
    enableOrderBy() {
      return this.status === "confirmed";
    },
    isGroupByShop() {
      return this.groupBy === "shop";
    },
    hashedItems() {
      const hashedItems = {};
      this.items.forEach(item => {
        if (Object.keys(hashedItems).includes(String(item.standard_id))) {
          hashedItems[item.standard_id].count += item.count;
        } else {
          hashedItems[item.standard_id] = {
            count: item.count,
            flower_specie: item.flower_specie,
            stem_size: item.stem_size,
            note: item.note,
          };
        }
      });
      return hashedItems;
    },
    groupedItems() {
      const groupedItems = [];
      Object.keys(this.hashedItems).forEach(arr => {
        groupedItems.push(this.hashedItems[arr]);
      });
      return groupedItems;
    }
  },
  async created() {
    const { query } = this.$route;
    const date = query.date || toDateKey(new Date());

    if (query.status) this.status = query.status;

    await this.fetchData(date);

    this.closestDate(date);

    if (date !== this.selectedDate) {
      await this.fetchData(this.selectedDate);
    }

    this.currentDate = new Date(this.selectedDate);
    this.createTableData();
  },
  methods: {
    async fetchData(date) {
      await this.$store.dispatch("orderItems/getOrderItems", {
        date,
        status: this.status
      });
    },
    async statusHandler(status) {
      this.status = status;

      if (this.currentDate) {
        const date = this.$date.YYYYMMDD(this.currentDate);

        await this.fetchData(date);

        this.selectedDate = this.closestNextWorkingDate;
        this.currentDate = new Date(this.selectedDate);

        if (date !== this.selectedDate) {
          await this.fetchData(this.selectedDate);
        }

        this.createTableData();
      }

      [this.backBtnLabel, this.nextBtnLabel] = statusBtnLabel(this, status);
      this.showCheckBox(status);
    },
    async groupByHandler(groupBy) {
      this.groupBy = groupBy;

      this.createTableData();
    },
    async dateHandler(d) {
      this.selectedDate = d;
      this.currentDate = new Date(d);
      this.$router.push({ name: this.$route.name, query: { date: d } });

      await this.fetchData(d);

      this.closestDate(d);

      this.createTableData();
    },
    async createTableData() {
      const { orderItems } = this.$store.state;

      this.items = [];

      const targetOrderItems = orderItems.listArr[this.status];

      if (this.status === "shipped") {
        orderItems.listArr.unloaded.map(id => targetOrderItems.push(id));
        orderItems.listArr.picked.map(id => targetOrderItems.push(id));
        orderItems.listArr.delivered.map(id => targetOrderItems.push(id));
      }

      targetOrderItems.forEach(id => {
        let storeOrderItems = this.store.list[this.status];
        if (this.status === "shipped") {
          storeOrderItems = {
            ...storeOrderItems,
            ...this.store.list.unloaded,
            ...this.store.list.picked,
            ...this.store.list.delivered
          };
        }

        const orderItem = storeOrderItems[id];

        if (
          this.$date.compareDates(this.currentDate, new Date(orderItem.flower_harvest.harvest_date))
        ) {
          if (this.isGroupByShop) {
            this.headers = [
              { text: this.$t("id"), value: "id", width: "60px" },
              { text: this.$t("min_count"), value: "min_count", width: "60px" },
              { text: this.$t("set_count"), value: "set_count", width: "60px" },
              { text: this.$t("unit_price"), value: "unit_price", width: "40px" },
              { text: this.$t("flower_specie"), value: "flower_specie", width: "80px" },
            ];
          } else if (this.groupBy === "order_item" && this.status === "confirmed") {
            this.headers = [
              { text: this.$t("flower_specie"), value: "flower_specie", width: "120px" },
              { text: this.$t("stem_size"), value: "stem_size", width: "80px" },
              { text: this.$t("count"), value: "count", width: "80px" },
              { text: this.$t("note"), value: "note", width: "500px" },
            ];
          } else {
            this.headers = [
              { text: this.$t("id"), value: "id", width: "60px" },
              { text: this.$t("min_count"), value: "min_count", width: "60px" },
              { text: this.$t("set_count"), value: "set_count", width: "60px" },
              { text: this.$t("unit_price"), value: "unit_price", width: "40px" },
              { text: this.$t("shop_name"), value: "shop_name", width: "80px" }
            ];
          }
          this.items.push({
            ...orderItem,
            shop_name: orderItem.shop.name,
            genus_name: orderItem.flower.flower_specie.flower_genus.name,
            flower_specie: orderItem.flower.flower_specie.name,
            unit_price: orderItem.harvest_price,
            min_count: orderItem.harvest_min_count,
            set_count: orderItem.lot_count,
            standard_id: orderItem.order_item_standard.id,
            count: orderItem.count,
            note: orderItem.order_item_standard.note,
            stem_size: `${orderItem.order_item_standard.stem_size_min}cm`,
            group_by_id: this.isGroupByShop ? orderItem.shop.id : orderItem.order_item_standard.id
          });

          this.items.sort((a, b) => a.standard_id - b.standard_id);
        }
      });
    },
    async backForwardHandler(side) {
      if (this.selectedItems.length) {
        if (this.status === "unconfirmed" && side === "backward") {
          this.reject();
        } else {
          this.accept(side);
        }
      }
    },
    closestDate(date) {
      const selectedDate = this.store.date_picker_dates.find(x => {
        if (x.substr(0, 8) === date.substr(0, 8)) {
          const day1 = Math.floor(date.split("-")[2]);
          const day2 = Math.floor(x.split("-")[2]);

          if (day1 <= day2) return true;
        }
        return false;
      });

      if (selectedDate) {
        this.selectedDate = selectedDate;
      } else if (this.store.date_picker_dates.length) {
        this.selectedDate = this.store.date_picker_dates[
          this.store.date_picker_dates.length - 1
        ];
      } else {
        this.selectedDate = null;
      }
    },
    onChecked(e) {
      if (e.item) {
        const { id } = e.item;
        if (e.value) {
          this.selectedItems.push(id);
        } else {
          this.selectedItems = this.selectedItems.filter(x => x !== id);
        }
      } else if (e.items) {
        e.items.forEach(item => {
          if (e.value) {
            this.selectedItems.push(item.id);
          } else {
            this.selectedItems = this.selectedItems.filter(
              id => id !== item.id
            );
          }
        });
      }
    },
    clickHandler(item) {
      this.$router.push({
        path: `/farm/order_items/${item.id}`,
        query: { date: this.$date.YYYYMMDD(this.currentDate), status: this.status }
      });
    },
    showCheckBox(status) {
      this.isShowCheckBox = ["unconfirmed"].includes(status);
    },
    accept(side) {
      const payload = {
        side,
        selectedItems: this.selectedItems,
        status: this.status
      };

      this.$store.dispatch("orderItems/postOrderItems", payload).then(res => {
        if (res) {
          this.$store
            .dispatch("orderItems/getOrderItems", {
              date: this.$date.YYYYMMDD(this.currentDate),
              status: this.status
            })
            .then(this.createTableData);
          this.$router.go({ path: this.$router.currentRoute.path, force: true });
        }
      });
    },
    reject() {
      const payload = {
        order_item_ids: this.selectedItems
      };
      this.$store.dispatch("orderItems/rejectRequest", payload).then(res => {
        if (res) {
          this.$store
            .dispatch("orderItems/getOrderItems", {
              date: this.$date.YYYYMMDD(this.currentDate),
              status: this.status
            })
            .then(this.createTableData);
          this.$router.go({ path: this.$router.currentRoute.path, force: true });
        }
      });
    }
  }
};
</script>

<style scoped>
.amouts {
  background: white;
  font: 1rem Noto Sans CJK JP;
  color: #777777;
}
.amout {
  font: Bold 1.1rem Noto Sans CJK JP;
  color: #007e57;
}
</style>
